import i18n from '@/utils/i18n';
import { ChatEditabilityConfig } from '@/experiences/sales-portal/domain/order-guides/blocs/utils/editability-utils';
import {
  OrderGuideCollection,
  OrderGuideCollectionKind,
} from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/types';
import { OrderGuideMergeStrategy } from '@/enums';
import {
  OrderGuidesListCollectionsQuery,
  SalesPortalOrderGuideCollectionType,
} from '@/types';
import { useGeneratedOrderGuidesListCollectionsQuery } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/blocs/documents/order-guides-list-collections.generated';
import { useMemo } from 'react';

const COLLECTION_KIND: Record<
  SalesPortalOrderGuideCollectionType,
  OrderGuideCollectionKind
> = {
  [SalesPortalOrderGuideCollectionType.Restaurant]: 'restaurant',
  [SalesPortalOrderGuideCollectionType.Supplier]: 'supplier',
};

type SalesPortalOrderGuideCollection =
  OrderGuidesListCollectionsQuery['getSalesPortalOrderGuideCollections']['collections'][number];

type OrderGuideRestaurantCollectionRow =
  OrderGuidesListCollectionsQuery['order_guide_restaurant_collection'][number];

type OrderGuideSupplierCollectionRow =
  OrderGuidesListCollectionsQuery['order_guide_supplier_collection'][number];

type OrderGuideRestaurantChat =
  | OrderGuidesListCollectionsQuery['chats'][number]
  | undefined;

const transformEditabilityConfig = (
  chat: OrderGuideRestaurantChat,
): ChatEditabilityConfig => ({
  chatUUID: chat?.uuid ?? null,
  customOrderGuideEditingEnabled:
    chat?.custom_order_guide_editing_enabled ?? false,
  orderGuideMergeStrategy:
    (chat?.order_guide_merge_strategy as OrderGuideMergeStrategy) ??
    (chat?.supplier?.supplier_metadata
      ?.order_guide_merge_strategy as OrderGuideMergeStrategy) ??
    OrderGuideMergeStrategy.OPERATOR_ONLY,
});

const transformOrderGuideCollection = (
  collection: SalesPortalOrderGuideCollection,
  restaurantCollectionByUUID: Map<string, OrderGuideRestaurantCollectionRow>,
  supplierCollectionByUUID: Map<string, OrderGuideSupplierCollectionRow>,
): OrderGuideCollection => {
  const restaurantCollection = restaurantCollectionByUUID.get(collection.uuid);
  const supplierCollection = supplierCollectionByUUID.get(collection.uuid);
  return {
    displayName: collection.is_default
      ? i18n.t('sales_portal.guide_selector.default')
      : (collection.name ?? i18n.t('order_guide')),
    groupCount:
      restaurantCollection?.order_guide_restaurant_partitions.length ??
      supplierCollection?.order_guide_supplier_partitions.length ??
      null,
    isDefault: collection.is_default,
    kind: COLLECTION_KIND[collection.type],
    linkedSupplierCollectionUUID:
      restaurantCollection?.linked_order_guide_supplier_collection_uuid ?? null,
    sortOrder: collection.sort_order ?? null,
    uuid: collection.uuid,
  };
};

interface UseOrderGuideCollectionsLoaderOptions {
  restaurantUUID?: string;
  supplierUUID?: string;
}

interface UseOrderGuideCollectionsLoaderResult {
  editabilityConfig: ChatEditabilityConfig;
  error: ReturnType<
    typeof useGeneratedOrderGuidesListCollectionsQuery
  >['error'];
  loading: boolean;
  orderGuides: OrderGuideCollection[];
  refetch: ReturnType<
    typeof useGeneratedOrderGuidesListCollectionsQuery
  >['refetch'];
}

/**
 * Loads a customer's order guides for the OG management screen,
 * fired once an account is selected. Queries `getSalesPortalOrderGuideCollections`
 * for the (restaurant, supplier) pair and shapes each row into the display data
 * the selector dropdown renders.
 *
 * The query is skipped until both `restaurantUUID` and `supplierUUID` are
 * known
 *
 * @param options - Loader configuration
 * @param options.restaurantUUID - Selected account's restaurant identifier
 * @param options.supplierUUID - Selected account's supplier identifier
 * @returns The customer's order guides, loading status, and any query error
 *
 * @example
 * ```ts
 * const { orderGuides, loading } = useOrderGuideCollectionsLoader({
 *   restaurantUUID,
 *   supplierUUID,
 * });
 * ```
 */
export const useOrderGuideCollectionsLoader = ({
  restaurantUUID,
  supplierUUID,
}: UseOrderGuideCollectionsLoaderOptions): UseOrderGuideCollectionsLoaderResult => {
  const skip = !restaurantUUID || !supplierUUID;

  const { data, loading, error, refetch } =
    useGeneratedOrderGuidesListCollectionsQuery({
      skip,
      variables: {
        restaurantUUID: restaurantUUID ?? '',
        supplierUUID: supplierUUID ?? '',
      },
    });

  const orderGuides = useMemo<OrderGuideCollection[]>(() => {
    const collections = data?.getSalesPortalOrderGuideCollections?.collections;
    if (!collections) {
      return [];
    }
    const restaurantCollectionByUUID = new Map(
      data.order_guide_restaurant_collection.map((collection) => [
        collection.uuid,
        collection,
      ]),
    );
    const supplierCollectionByUUID = new Map(
      data.order_guide_supplier_collection.map((collection) => [
        collection.uuid,
        collection,
      ]),
    );
    return collections.map((collection) =>
      transformOrderGuideCollection(
        collection,
        restaurantCollectionByUUID,
        supplierCollectionByUUID,
      ),
    );
  }, [data]);

  const editabilityConfig = useMemo<ChatEditabilityConfig>(
    () => transformEditabilityConfig(data?.chats[0]),
    [data],
  );

  return { editabilityConfig, error, loading, orderGuides, refetch };
};
